import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'
import {
    classToFComponent,
    FCClassContext,
    ToolbarDivider,
    ToolbarSectionTitleResponsive,
    ToolbarToggleRow
} from 'src/utils/views'

import FormGroup from '@mui/material/FormGroup'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'

import { SearchBoxView } from 'src/components/searchbox/views'
import { HeaderScope } from '../ta_scopes'

const LOG = Logger.get('TheActingHeaderView')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        flexGrow: 1,
        gap: 12,
        marginLeft: 5,
        minWidth: 0,
        width: 0
    },
    searchBar: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexGrow: 1,
        minWidth: 220,
        width: 0,
        '@media (max-width: 720px)': { display: 'none' }
    },
    searchBox: {
        flexGrow: 1,
        minWidth: 0,
        '& > div:first-of-type': {
            minHeight: 43,
            padding: '0 5px 0 10px',
            borderRadius: 9,
            border: '1px solid rgba(255, 255, 255, .18)',
            backgroundColor: 'rgba(255, 255, 255, .06)',
            transition: 'border-color .15s ease, box-shadow .15s ease',
            '&:focus-within': {
                borderColor: 'var(--green-700)',
                boxShadow: '0 0 0 3px rgba(38, 229, 31, .12)'
            }
        },
        '& > div:first-of-type > div': {
            minWidth: 0,
            minHeight: 41,
            gap: 4,
            '&.focused': { boxShadow: 'none', borderColor: 'transparent' }
        },
        '& input': {
            backgroundColor: 'transparent',
            color: '#f4f4f4',
            fontSize: 14,
            '&::placeholder': { color: 'rgba(255, 255, 255, .52)', opacity: 1 }
        },
        '& .MuiInputBase-root': { color: '#f4f4f4', fontSize: 13 },
        '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, .62)' },
        '& [data-tag-index]': {
            height: 25,
            borderRadius: 999,
            border: '1px solid rgba(255, 255, 255, .2)',
            backgroundColor: 'rgba(255, 255, 255, .1)',
            color: '#f4f4f4',
            fontSize: 11.5,
            fontWeight: 700,
            '& svg': { color: 'rgba(255, 255, 255, .68)' },
            '& svg:hover': { color: '#fff' }
        },
        '& > div:not(:first-of-type)': {
            padding: 7,
            borderRadius: 10,
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-2)',
            color: 'var(--ink)',
            fontSize: 14
        }
    },
    adornment: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flex: '0 0 auto'
    }
})

type HeaderProps = {
    className?: string
    scope: HeaderScope
}

class HeaderViewClass implements FCClassContext<HeaderProps> {
    // :: Fields

    scope!: HeaderScope

    // :: Emissors

    readonly emitOnlyActivesChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onOnlyActivesChange(e.target.checked).catch(LOG.caught)

    readonly emitOnlySemVinculoChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onOnlySemVinculoChange(e.target.checked).catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: HeaderProps) {
        this.scope = scope
    }

    render({ className, scope }: HeaderProps): React.JSX.Element {
        const classes = useStyles().classes

        return (
            <div className={clsx(classes.view, className)}>
                <ToolbarDivider orientation="vertical" variant="middle" flexItem />
                <ToolbarSectionTitleResponsive>Profissionais Médicos</ToolbarSectionTitleResponsive>
                <div className={classes.searchBar}>
                    <SearchBoxView
                        className={classes.searchBox}
                        scope={scope.search}
                        endAdornment={
                            <div className={classes.adornment}>
                                <FormGroup>
                                    <Tooltip title="Deixa de fora as empresas com situação cadastral inativa.">
                                        <ToolbarToggleRow
                                            label="Mostrar ativos"
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={scope.onlyActives}
                                                    onChange={this.emitOnlyActivesChange}
                                                />
                                            }
                                        />
                                    </Tooltip>
                                    <Tooltip title="Mostra apenas profissionais que não têm vínculo com nenhuma empresa.">
                                        <ToolbarToggleRow
                                            label="Somente sem vínculo"
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={scope.onlySemVinculo}
                                                    onChange={this.emitOnlySemVinculoChange}
                                                />
                                            }
                                        />
                                    </Tooltip>
                                </FormGroup>
                            </div>
                        }
                    />
                </div>
            </div>
        )
    }
}
export const HeaderView = classToFComponent(HeaderViewClass, React)
export default HeaderView
